import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Building2, Lock } from 'lucide-react';
import { GET } from '../services/api';

/**
 * Super-admin "master" chapter filter. Picked once in the admin header, it locks every
 * page's own city/chapter filter to that chapter. Other roles never see it, and for them
 * useMasterChapter() always reports no selection, so their pages behave as before.
 * Persisted in localStorage; logout's localStorage.clear() resets it.
 */
const STORAGE_KEY = 'masterChapter';
const EMPTY = { chapterId: '', chapterName: '', cityId: '', cityName: '' };

const MasterChapterContext = createContext({ ...EMPTY, isSuperAdmin: false, setMaster: () => {} });

const readUserRole = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return null; }
};

const readStored = () => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }; } catch { return EMPTY; }
};

export const MasterChapterProvider = ({ children }) => {
    const isSuperAdmin = readUserRole() === 'super-admin';
    const [master, setMasterState] = useState(() => (isSuperAdmin ? readStored() : EMPTY));

    const setMaster = (next) => {
        const value = next ? { ...EMPTY, ...next } : EMPTY;
        setMasterState(value);
        try {
            if (value.chapterId) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
            else localStorage.removeItem(STORAGE_KEY);
        } catch { /* storage unavailable: selection just won't survive a reload */ }
    };

    const value = useMemo(
        () => ({ ...(isSuperAdmin ? master : EMPTY), isSuperAdmin, setMaster }),
        [master, isSuperAdmin] // eslint-disable-line react-hooks/exhaustive-deps
    );
    return <MasterChapterContext.Provider value={value}>{children}</MasterChapterContext.Provider>;
};

/** { chapterId, chapterName, cityId, cityName } — all '' when nothing is selected or the user isn't super-admin. */
export const useMasterChapter = () => useContext(MasterChapterContext);

/** Header dropdown. Renders nothing for non-super-admins. */
export const MasterChapterSelect = () => {
    const { isSuperAdmin, chapterId, setMaster } = useMasterChapter();
    const [chapters, setChapters] = useState([]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        GET('/dashboard/chapters')
            .then(list => setChapters(Array.isArray(list) ? list : []))
            .catch(() => setChapters([]));
    }, [isSuperAdmin]);

    // Group by city for the <optgroup>s
    const byCity = useMemo(() => {
        const groups = {};
        chapters.forEach(ch => {
            const city = ch.city_id?.name || 'Other';
            (groups[city] = groups[city] || []).push(ch);
        });
        return Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([city, list]) => [city, list.sort((a, b) => a.name.localeCompare(b.name))]);
    }, [chapters]);

    if (!isSuperAdmin) return null;

    const handleChange = (e) => {
        const ch = chapters.find(c => c._id === e.target.value);
        setMaster(ch ? {
            chapterId: ch._id,
            chapterName: ch.name,
            cityId: ch.city_id?._id || ch.city_id || '',
            cityName: ch.city_id?.name || '',
        } : null);
    };

    return (
        <label className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-bold transition-colors
            ${chapterId ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'}`}>
            <Building2 className="w-4 h-4 shrink-0" />
            <span className="sr-only">Master chapter filter</span>
            <select value={chapterId} onChange={handleChange}
                className="bg-transparent outline-none cursor-pointer max-w-[9rem] sm:max-w-[14rem] truncate">
                <option value="">All chapters</option>
                {byCity.map(([city, list]) => (
                    <optgroup key={city} label={city}>
                        {list.map(ch => <option key={ch._id} value={ch._id}>{ch.name}</option>)}
                    </optgroup>
                ))}
            </select>
        </label>
    );
};

/** Small note shown next to a page's filters while they are locked by the master filter. */
export const MasterChapterLockNote = () => {
    const { chapterId, chapterName } = useMasterChapter();
    if (!chapterId) return null;
    return (
        <p className="flex items-center gap-1.5 text-xs font-medium text-blue-600">
            <Lock className="w-3.5 h-3.5" /> Filtered by {chapterName} — change it from the chapter filter at the top.
        </p>
    );
};
